import React, { useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client'
import { getModalUrl } from 'lib/modals'
import { logException } from 'lib/errors'
import cookies from 'lib/cookies'
import useAssert from 'lib/hooks/useAssert'
import { getLocale } from 'lib/utils/getLocale'

import Button from 'components/UI/Button'
import Link from 'components/Link'
import ErrorComponent from 'components/Errors/ErrorComponent'

import sendVerificationCode from 'graphql/user/sendVerificationCode.gql'

import './user-transfer-phone-number.pcss'

const UserTransferPhoneNumber = ({
    onCancel,
    onSubmitted
}) => {
    const location = useLocation()
    const history = useHistory()
    const [sendPhoneVerificationCode, { loading: sendPhoneVerificationCodeLoading, error: sendPhoneVerificationCodeError }] = useMutation(sendVerificationCode)

    const [userTransferPhoneNumber] = useState(cookies.get('user-transfer-phone-number'))
    useAssert(userTransferPhoneNumber, 'Missing required cookie "userTransferPhoneNumber"')

    const phoneNumber = userTransferPhoneNumber && userTransferPhoneNumber.phoneNumber
    const activeAdsExist = userTransferPhoneNumber && userTransferPhoneNumber.activeAdsExist
    const preventHijack = userTransferPhoneNumber && userTransferPhoneNumber.preventHijack

    useAssert(phoneNumber, 'Missing required param "phoneNumber"')

    if (!userTransferPhoneNumber && !phoneNumber && !activeAdsExist) {
        onCancel()
    }

    const { t, i18n } = useTranslation('editProfile')

    const sendVerifyCode = async () => {
        await sendPhoneVerificationCode({
            variables: {
                mobileNumber: phoneNumber,
                locale: i18n.language
            }
        }).then((result) => {
            try {
                history.push(getModalUrl(location, 'kayttaja/puhelinnumero/vahvista'))
            } catch (e) {
                logException(e)
            }
            onSubmitted && onSubmitted()
            return Promise.resolve(result)
        }).catch((error) => {
            return Promise.reject(error)
        })
    }

    return (
        <div className="user-transfer-phone-number">
            {activeAdsExist === true || preventHijack === true
                ? (
                    <>
                        <div className="user-transfer-phone-number__content">
                            <p>{t('editProfile:transferPhoneNumber.description.paragraph1', { phoneNumber: phoneNumber })}</p>
                            <p>
                                <Trans i18nKey="editProfile:transferPhoneNumber.description.paragraph2">
                                    Jos myös toinen käyttäjätili kuuluu sinulle, saat numeron siirrettyä, kunhan olet ensin poistanut aktiiviset ilmoitukset. Muussa tapauksessa, ole yhteydessä asiakaspalveluumme puh.
                                </Trans>
                                <Link
                                    to={`tel:${t('editProfile:transferPhoneNumber.description.phoneNumber').replace(/\D+/g, '')}`}
                                    className="button button--tertiary"
                                    trackName="phoneNumber"
                                >
                                    {t('editProfile:transferPhoneNumber.description.phoneNumber')}
                                </Link>
                            </p>
                        </div>
                        <div className="user-transfer-phone-number__action">
                            <Button
                                trackName="close"
                                className="button button--secondary"
                                type="button"
                                onClick={() => onCancel()}
                            >
                                {t('common:close')}
                            </Button>
                        </div>
                    </>
                )
                : (
                    <>
                        <div className="user-transfer-phone-number__content">
                            <p>{t('editProfile:transferPhoneNumber.transferNumberDescription', { phoneNumber: phoneNumber })}</p>
                        </div>
                        <div className="user-transfer-phone-number__action">
                            <Button
                                type="button"
                                className="button"
                                trackName="sendVerifyCode"
                                onClick={sendVerifyCode}
                                loading={sendPhoneVerificationCodeLoading}
                                disabled={sendPhoneVerificationCodeLoading}
                            >
                                {t('editProfile:transferPhoneNumber.action.yes')}
                            </Button>
                            <Button
                                type="button"
                                className="button button--secondary"
                                trackName="cancel"
                                disabled={sendPhoneVerificationCodeLoading}
                                onClick={() => onCancel()}
                            >
                                {t('editProfile:transferPhoneNumber.action.no')}
                            </Button>
                            {sendPhoneVerificationCodeError && (
                                <ErrorComponent
                                    error={sendPhoneVerificationCodeError}
                                    container={false}
                                    contentOverride={(appError) => {
                                        if (appError.code === 1000) {
                                            return (
                                                <Trans i18nKey="editProfile:details.primary.fields.mobile.errorMessage">
                                                    Tarkista puhelinnumero. Ongelmatilanteissa <Link to={`${getLocale(i18n.language)}/ota-yhteytta#asiakaspalvelu`} trackName="customerService">ota yhteyttä asiakaspalveluun</Link>
                                                </Trans>
                                            )
                                        }
                                    }}
                                />
                            )}
                        </div>
                    </>
                )}
        </div>
    )
}

export default UserTransferPhoneNumber
